import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { LOGO_URL, API } from "@/config";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Trophy, Search, Globe, ArrowRight, Calendar, Loader2 } from "lucide-react";

const SITE = "https://creatara.ai";

export default function LeaderboardPage() {
  const [data, setData] = useState({ items: [], last_refresh: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/leaderboard`)
      .then((r) => setData(r.data))
      .catch(() => { /* fail silent; show empty state */ })
      .finally(() => setLoading(false));
  }, []);

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "AI Search Citation Leaderboard",
    description: "Live ranking of brands cited by AI search engines for high-intent solopreneur queries.",
    itemListElement: data.items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${SITE}/leaderboard/${item.slug}`,
      name: item.query_text,
    })),
  };

  return (
    <>
      <Helmet>
        <title>AI Search Citation Leaderboard — Live Brand Rankings | Creatara</title>
        <meta name="description" content="Live leaderboard of brands cited by AI search engines (ChatGPT, Perplexity, Gemini) for the top solopreneur queries. Updated daily via Gemini Google-Search grounding." />
        <link rel="canonical" href={`${SITE}/leaderboard`} />
        <meta property="og:title" content="AI Search Citation Leaderboard — Live Rankings" />
        <meta property="og:description" content="Which brands are cited by AI search engines for the queries solopreneurs ask. Live data, updated daily." />
        <meta property="og:url" content={`${SITE}/leaderboard`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-background">
        <nav className="fixed top-0 w-full z-50 glass">
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Creatara" className="w-10 h-10 rounded-lg" />
              <span className="font-bold text-lg">Creatara</span>
            </Link>
            <Link to="/auth"><Button size="sm" className="rounded-full bg-violet-600 hover:bg-violet-700" data-testid="leaderboard-nav-cta">Try Free</Button></Link>
          </div>
        </nav>

        <div className="max-w-6xl mx-auto px-6 pt-32 pb-20" data-testid="leaderboard-page">
          {/* Hero */}
          <div className="text-center mb-12">
            <Badge variant="secondary" className="mb-4"><Trophy className="w-3 h-3 mr-1" /> Live Data</Badge>
            <h1 className="text-4xl sm:text-6xl font-bold mb-5 leading-tight">
              AI Search Citation<br />Leaderboard
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-6 leading-relaxed">
              Live rankings of brands cited by AI search engines for high-intent solopreneur queries.
              Powered by <strong className="text-blue-300">Gemini 2.5 Flash with Google Search grounding</strong>.
            </p>
            {data.last_refresh && (
              <p className="text-sm text-muted-foreground inline-flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" /> Last refreshed {new Date(data.last_refresh).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
                <span className="mx-2">·</span>
                <span>Refreshes daily at 04:00 UTC</span>
              </p>
            )}
          </div>

          {/* Leaderboard cards */}
          {loading ? (
            <div className="text-center py-20"><Loader2 className="w-8 h-8 animate-spin mx-auto text-violet-400" /></div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="leaderboard-grid">
              {data.items.map((item) => {
                const snap = item.snapshot;
                const mentions = snap?.all_mentions || [];
                const sources = snap?.grounding_sources || [];
                return (
                  <Card key={item.slug} className="bg-white/5 border-white/10 hover:border-violet-500/40 transition-colors" data-testid={`leaderboard-card-${item.slug}`}>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between mb-3 gap-3">
                        <div className="flex-1 min-w-0">
                          <Badge variant="secondary" className="text-[10px] uppercase mb-2">{item.category}</Badge>
                          <h2 className="font-bold text-lg leading-snug">{item.query_text}</h2>
                        </div>
                        {snap && (
                          <Badge className="bg-blue-600/30 text-blue-200 border border-blue-500/40 text-[10px] flex-shrink-0">
                            🌐 Live
                          </Badge>
                        )}
                      </div>

                      {mentions.length === 0 ? (
                        <p className="text-sm text-muted-foreground italic py-4">No snapshot yet — refreshing on next cron.</p>
                      ) : (
                        <>
                          <p className="text-xs font-semibold text-muted-foreground mb-2">Top cited brands</p>
                          <ol className="space-y-1.5 mb-4">
                            {mentions.slice(0, 5).map((m, idx) => {
                              const src = sources.find((s) => s.domain === m);
                              return (
                                <li key={`${m}-${idx}`} className="flex items-center gap-2 text-sm">
                                  <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${idx === 0 ? "bg-amber-500/30 text-amber-200" : idx === 1 ? "bg-zinc-400/30 text-zinc-200" : idx === 2 ? "bg-orange-600/30 text-orange-200" : "bg-white/5 text-muted-foreground"}`}>
                                    {idx + 1}
                                  </span>
                                  {src?.uri ? (
                                    <a href={src.uri} target="_blank" rel="noopener noreferrer" className="font-mono text-sm hover:text-blue-300 transition-colors truncate">{m}</a>
                                  ) : (
                                    <span className="font-mono text-sm truncate">{m}</span>
                                  )}
                                </li>
                              );
                            })}
                          </ol>
                          {mentions.length > 5 && (
                            <p className="text-xs text-muted-foreground mb-3">+{mentions.length - 5} more</p>
                          )}
                        </>
                      )}

                      <Link to="/auth" className="inline-flex items-center gap-1 text-sm font-semibold text-violet-400 hover:text-violet-300 transition-colors" data-testid={`leaderboard-cta-${item.slug}`}>
                        Track your brand for this query <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Trust section */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { Icon: Globe, title: "Real live web data", body: "Every snapshot uses Gemini 2.5 Flash with native Google Search grounding — not training-cutoff knowledge." },
              { Icon: Search, title: "Same engine as your tracker", body: "Creatara Pro users get this exact engine for unlimited custom queries inside their dashboard." },
              { Icon: Trophy, title: "Daily refresh, public data", body: "Curated queries refresh at 04:00 UTC. No PII, no customer data — only public competitor citations." },
            ].map((f, i) => (
              <div key={i} className="rounded-xl bg-white/5 border border-white/10 p-5">
                <f.Icon className="w-6 h-6 text-violet-400 mb-2" />
                <p className="font-bold mb-1">{f.title}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-12 rounded-xl bg-gradient-to-br from-violet-600/20 to-amber-600/10 border border-violet-500/30 p-10 text-center">
            <h2 className="text-3xl font-bold mb-2">Not on the leaderboard for your niche?</h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">Track your own brand for any query. Get a Get Cited Action Plan. Watch the rank climb week-over-week.</p>
            <Link to="/auth"><Button size="lg" className="rounded-full bg-violet-600 hover:bg-violet-700" data-testid="leaderboard-cta-bottom">Start tracking — £29/mo →</Button></Link>
            <p className="text-xs text-muted-foreground mt-3"><Link to="/citation-tracker" className="underline hover:text-white">See how the Citation Tracker works →</Link></p>
          </div>
        </div>
      </div>
    </>
  );
}
